import React, { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import './mobileShiftReportPDF.css';

const MobileShiftReportPDF = forwardRef(({ selectedShift, showDetails = true }, ref) => {
  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const companyProfile = useSelector((state) => state.companyProfileSlice?.userValue);
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  
  const {
    shiftReportDetails,
    billTypeSummary,
    deliveryPartnerSummary,
    deliveryBoysSummary,
    discountSummary,
    categorySalesSummary,
    taxDetails,
    shiftReporCheckinSummary
  } = useSelector((state) => state.posReportshiftSlice);

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  const shiftId = shiftReportDetails?.shiftSeq || selectedShift?.shiftSeq || selectedShift?.shiftId || 'N/A';
  const employeeName = shiftReportDetails?.employeeName || userInfo?.userName || 'Unknown';
  const startDate = shiftReportDetails?.startDate || selectedShift?.startDate || '';
  const startTime = shiftReportDetails?.startTime || selectedShift?.STARTTIME || '';
  const endDate = shiftReportDetails?.endDate || '';
  const endTime = shiftReportDetails?.endTime || '';
  const isActiveShift = !shiftReportDetails?.endDate;

  // Calculate totals
  const { dineIn, takeAway, delivery, selfDelivery, aggregator, cashAndCard, staffExpense, outletExpense, refund, deliveryCharge, packagingCharge } = billTypeSummary || {};
  
  const totalSales = (dineIn?.total || 0) + (takeAway?.total || 0) + (delivery?.total || 0);
  const categoryTotal = Array.isArray(categorySalesSummary) ? categorySalesSummary.reduce((sum, cat) => sum + (cat.totalAmount || cat.total || 0), 0) : 0;
  const taxTotal = Array.isArray(taxDetails) ? taxDetails.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0) : 0;
  const discountTotal = Array.isArray(discountSummary) ? discountSummary.reduce((sum, d) => sum + (d.total || d.amount || 0), 0) : 0;
  const partnerTotal = Array.isArray(deliveryPartnerSummary) ? deliveryPartnerSummary.reduce((sum, p) => sum + (p.total || 0), 0) : 0;
  const boysTotal = Array.isArray(deliveryBoysSummary) ? deliveryBoysSummary.reduce((sum, b) => sum + (b.total || 0), 0) : 0;

  return (
    <div ref={ref} className="shift-report-pdf-container">
      {/* Header Section */}
      <div className="pdf-header">
        {companyProfile?.logo && (
          <img src={companyProfile.logo} alt="Logo" className="company-logo" />
        )}
        <h1 className="company-name">{companyProfile?.companyName || 'Restaurant'}</h1>
        <p className="company-address">
          {companyProfile?.companyAddress}<br />
          {companyProfile?.country}<br />
          {companyProfile?.companyEmail} | {companyProfile?.contactNumber}
        </p>
      </div>

      <div className="pdf-title">
        <h2>ADVANCE SHIFT REPORT</h2>
      </div>

      {/* Shift Details */}
      <div className="shift-details-section">
        <table className="shift-info-table">
          <tbody>
            <tr>
              <td><strong>Shift</strong></td>
              <td>: {shiftId} - {employeeName}</td>
              <td><strong>Start</strong></td>
              <td>: {startDate} {startTime}</td>
            </tr>
            <tr>
              <td><strong>Branch</strong></td>
              <td>: {shiftReportDetails?.branchName || userInfo?.branchName || ''}</td>
              <td><strong>End</strong></td>
              <td>: {isActiveShift ? 'Active Shift' : `${endDate} ${endTime}`}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bill Type Summary */}
      {billTypeSummary && (
        <div className="pdf-section">
          <h3 className="section-title">BILL TYPE SUMMARY</h3>
          
          {/* Dine In */}
          {dineIn?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Dine In ({dineIn?.list?.length || 0})</span>
                <span>{formatAmount(dineIn?.total)}</span>
              </div>
              {showDetails && dineIn?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dineIn.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.billNo}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Take Away */}
          {takeAway?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Take Away ({takeAway?.list?.length || 0})</span>
                <span>{formatAmount(takeAway?.total)}</span>
              </div>
              {showDetails && takeAway?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {takeAway.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.billNo}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Self Delivery */}
          {selfDelivery?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Self Delivery ({selfDelivery?.list?.length || 0})</span>
                <span>{formatAmount(selfDelivery?.total)}</span>
              </div>
              {showDetails && selfDelivery?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selfDelivery.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.billNo}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Aggregator */}
          {aggregator?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Aggregator ({aggregator?.list?.length || 0})</span>
                <span>{formatAmount(aggregator?.total)}</span>
              </div>
              {showDetails && aggregator?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Aggregator</th>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggregator.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.aggregatorName}</td>
                        <td>{item.billNo}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Staff Expense */}
          {staffExpense?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Staff Expense ({staffExpense?.list?.length || 0})</span>
                <span>{formatAmount(staffExpense?.total)}</span>
              </div>
              {showDetails && staffExpense?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Expense</th>
                      <th>Employee</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffExpense.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.expense}</td>
                        <td>{item.employee}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Outlet Expense */}
          {outletExpense?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Outlet Expense ({outletExpense?.list?.length || 0})</span>
                <span>{formatAmount(outletExpense?.total)}</span>
              </div>
              {showDetails && outletExpense?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Doc No</th>
                      <th>Expense</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outletExpense.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.docNo}</td>
                        <td>{item.expense}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Refund */}
          {refund?.total > 0 && (
            <div className="bill-type-block">
              <div className="bill-type-header-row">
                <span>Refund ({refund?.list?.length || 0})</span>
                <span>{formatAmount(refund?.total)}</span>
              </div>
              {showDetails && refund?.list?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Product</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refund.list.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.billNo}</td>
                        <td>{item.product}</td>
                        <td>{item.date}</td>
                        <td className="text-right">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="bill-type-total">
            <span><strong>Total Sales</strong></span>
            <span><strong>{formatAmount(totalSales)}</strong></span>
          </div>
        </div>
      )}

      {/* Cash & Card Summary */}
      {cashAndCard?.list?.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">PAYMENT SUMMARY</h3>
          <table className="pdf-table">
            <thead>
              <tr>
                <th>Payment Method</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Commission</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {cashAndCard.list.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.type}</td>
                  <td className="text-right">{formatAmount(item.amount)}</td>
                  <td className="text-right">{formatAmount(item.commission)}</td>
                  <td className="text-right">{formatAmount(item.balance)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Total</strong></td>
                <td className="text-right"><strong>{formatAmount(cashAndCard?.total)}</strong></td>
                <td className="text-right">-</td>
                <td className="text-right">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Category Sales Summary */}
      {Array.isArray(categorySalesSummary) && categorySalesSummary.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">CATEGORY SALES SUMMARY</h3>
          {categorySalesSummary.map((cat, idx) => (
            <div key={idx} className="bill-type-block">
              <div className="bill-type-header-row">
                <span>{cat.categoryName || cat._id}</span>
                <span>{formatAmount(cat.totalAmount || cat.total)}</span>
              </div>
              {showDetails && cat.products?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.products.map((prod, pIdx) => (
                      <tr key={pIdx}>
                        <td>{prod.product || prod.productName}</td>
                        <td className="text-center">{prod.quantity || prod.qty || 0}</td>
                        <td className="text-right">{formatAmount(prod.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="bill-type-total">
            <span><strong>Category Total</strong></span>
            <span><strong>{formatAmount(categoryTotal)}</strong></span>
          </div>
        </div>
      )}

      {/* Tax Details */}
      {taxDetails?.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">TAX DETAILS</h3>
          <table className="pdf-table">
            <thead>
              <tr>
                <th>Tax Name</th>
                <th className="text-center">%</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {taxDetails.map((tax, idx) => (
                <tr key={idx}>
                  <td>{tax.taxName || tax._id}</td>
                  <td className="text-center">{tax.taxPercentage || '-'}</td>
                  <td className="text-right">{formatAmount(tax.taxAmount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Total Tax</strong></td>
                <td className="text-center">-</td>
                <td className="text-right"><strong>{formatAmount(taxTotal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Delivery Partners */}
      {Array.isArray(deliveryPartnerSummary) && deliveryPartnerSummary.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">DELIVERY PARTNERS</h3>
          {deliveryPartnerSummary.map((partner, idx) => (
            <div key={idx} className="bill-type-block">
              <div className="bill-type-header-row">
                <span>{partner.partner || partner.name || partner._id} ({partner.data?.length || 0})</span>
                <span>{formatAmount(partner.total)}</span>
              </div>
              {showDetails && partner.data?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Order No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partner.data.map((order, oIdx) => (
                      <tr key={oIdx}>
                        <td>{order.orderNo || order.billNo}</td>
                        <td>{order.date}</td>
                        <td className="text-right">{formatAmount(order.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="bill-type-total">
            <span><strong>Total</strong></span>
            <span><strong>{formatAmount(partnerTotal)}</strong></span>
          </div>
        </div>
      )}

      {/* Delivery Boys */}
      {Array.isArray(deliveryBoysSummary) && deliveryBoysSummary.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">DELIVERY BOYS</h3>
          {deliveryBoysSummary.map((boy, idx) => (
            <div key={idx} className="bill-type-block">
              <div className="bill-type-header-row">
                <span>{boy.partner || boy.name || boy._id} ({boy.data?.length || 0})</span>
                <span>{formatAmount(boy.total)}</span>
              </div>
              {showDetails && boy.data?.length > 0 && (
                <table className="pdf-table detail-table">
                  <thead>
                    <tr>
                      <th>Order No</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boy.data.map((order, oIdx) => (
                      <tr key={oIdx}>
                        <td>{order.orderNo || order.billNo}</td>
                        <td>{order.date}</td>
                        <td className="text-right">{formatAmount(order.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          <div className="bill-type-total">
            <span><strong>Total</strong></span>
            <span><strong>{formatAmount(boysTotal)}</strong></span>
          </div>
        </div>
      )}

      {/* Discounts */}
      {discountSummary?.length > 0 && (
        <div className="pdf-section">
          <h3 className="section-title">DISCOUNT SUMMARY</h3>
          <table className="pdf-table">
            <thead>
              <tr>
                <th>Discount</th>
                <th className="text-center">Count</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {discountSummary.map((disc, idx) => (
                <tr key={idx}>
                  <td>{disc.name || disc._id || 'Discount'}</td>
                  <td className="text-center">{disc.count || 0}</td>
                  <td className="text-right">{formatAmount(disc.total || disc.amount)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Total Discount</strong></td>
                <td className="text-center">-</td>
                <td className="text-right"><strong>{formatAmount(discountTotal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="pdf-footer">
        <p>Generated on: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
});

MobileShiftReportPDF.displayName = 'MobileShiftReportPDF';

export default MobileShiftReportPDF;
